import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  effect,
  inject,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { Modal, ModalConfig, ModalType } from '@app/shared/models/modal.model';
import { EventManager } from '@app/shared/utils/event-manager';
import { AssetsService } from '@app/shared/services/assets.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { OneSignalService, TagName } from '@app/shared/services/one-signal.service';
import { AppService } from '@app/app.service';
import { AppsFlyersService } from '@shared/services/appsFlyers.service';
import { SharedModule } from '@app/shared/shared.module';
import { AnalyticsService } from '@app/shared/services/analytics.service';
import { GamesRepository } from '@app/shared/state/games/games.repository';
import { JulietBuddyComponent } from '@app/shared/components/juliet-buddy/juliet-buddy.component';
import { START_NOW_MODAL_POSE } from '@app/shared/components/juliet-buddy/juliet-buddy.config';
import { GsapService } from '@app/shared/animations/gsap.service';

export enum AnimationStep {
  Start = 0,
  ShowModalContent = 1,
  SignboardDisplacement = 2,
}

@Component({
  selector: 'slotcom-start-now-modal',
  templateUrl: './start-now.component.html',
  styleUrls: ['./start-now.component.scss'],
  standalone: true,
  imports: [SharedModule, JulietBuddyComponent],
})
export class StartNowOnboardingComponent implements OnInit, AfterViewInit {
  startNowModalData = inject<ModalConfig>(MAT_DIALOG_DATA);
  translateService = inject(TranslateService);
  private eventManager = inject(EventManager);
  private assetsService = inject(AssetsService);
  logger = inject(LoggerService);
  private oneSignalService = inject(OneSignalService);
  private appService = inject(AppService);
  private analyticsService = inject(AnalyticsService);
  private appsFlyersService = inject(AppsFlyersService);
  private gamesRepo = inject(GamesRepository);
  private gsapService = inject(GsapService);

  currentStep = signal<AnimationStep>(AnimationStep.Start);
  private timerId?: ReturnType<typeof setTimeout>;

  private readonly STEP_DURATIONS: Record<AnimationStep, number> = {
    [AnimationStep.Start]: 100,
    [AnimationStep.ShowModalContent]: 500,
    [AnimationStep.SignboardDisplacement]: 0,
  };

  @ViewChild('juliet') juliet!: ElementRef<HTMLDivElement>;
  @ViewChild('signboard') signboard!: ElementRef<HTMLDivElement>;

  gameId: string;
  gameIcon: object;
  currentLanguage: string;

  readonly startNowModalPose = START_NOW_MODAL_POSE;

  private readonly _ = effect(() => {
    const step = this.currentStep();
    this.handleStep(step);
  });

  private handleStep(step: AnimationStep): void {
    this.clearTimer();

    switch (step) {
      case AnimationStep.Start:
        break;
      case AnimationStep.SignboardDisplacement:
        this.gsapService.floatingHorizontal(this.signboard.nativeElement, -25, 1);
        break;
      case AnimationStep.ShowModalContent:
        this.gsapService.fromTo(
          this.signboard.nativeElement,
          { opacity: 0, scale: 0 },
          { opacity: 1, scale: 1 },
          { duration: 0.5 }
        );

        this.gsapService.slideIn(this.juliet.nativeElement, 'bottom', 1000, { duration: 0.7 });

        this.scheduleNext(step);
        break;
    }
  }

  private clearTimer(): void {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = undefined;
    }
  }

  private scheduleNext(step: AnimationStep): void {
    const duration = this.STEP_DURATIONS[step];
    if (duration > 0) {
      this.timerId = setTimeout(() => {
        if (this.currentStep() < AnimationStep.SignboardDisplacement) {
          this.currentStep.set(this.currentStep() + 1);
        }
      }, duration);
    }
  }

  constructor() {
    this.gameId = this.startNowModalData.properties.gameId;
    inject(DestroyRef).onDestroy(() => this.clearTimer());
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      this.resetStage();
      this.currentStep.set(AnimationStep.ShowModalContent);
    });
  }

  private resetStage(): void {
    this.setElements([this.signboard, this.juliet], { opacity: 0 });
  }
  private setElements(refs: (ElementRef<HTMLElement> | undefined)[], props: Record<string, unknown>): void {
    for (const ref of refs) {
      if (ref?.nativeElement) {
        this.gsapService.set(ref.nativeElement, props);
      }
    }
  }

  ngOnInit() {
    this.currentLanguage = this.translateService.currentLang;

    if (!this.gameId) {
      this.gameId = this.gamesRepo.getAllGames()[0].gameId;
    }

    this.assetsService.gamesVersion$.subscribe({
      next: (versions) => {
        const url = this.assetsService.getGameIconUrlLegacy(this.gameId, versions);
        this.gameIcon = { 'background-image': `url('${url}')` };
      },
      error: (error) => this.logger.error(error),
    });
  }

  closeModal() {
    this.analyticsService.logEventAndForget('ps_active_registered_user');
    this.appsFlyersService.logAnalyticsEvent('ps_active_registered_user').catch((e) => this.logger.error(e));

    this.oneSignalService.sendTag(TagName.OnboardingSteps, 3).catch((e) => this.logger.error(e));

    this.eventManager.closeModal(
      new Modal(this.startNowModalData.id, ModalType.Persisted, this.startNowModalData, '', true)
    );

    this.appService.showGame(this.gameId);
  }
}
